import React from "react";
import SweetAlert from "react-bootstrap-sweetalert";
import { useDispatch } from "react-redux";

/**
 * Reusable Generic Error Alert Component
 * 
 * @param {Object} props
 * @param {boolean} props.show - Whether to show the alert
 * @param {string} props.title - Error message title
 * @param {string} props.errorCode - Error code to display
 * @param {Function} props.onConfirm - Custom confirm handler (optional)
 * @param {string} props.confirmBtnBsStyle - Button style (default: "danger")
 * @param {number} props.timeout - Auto-close timeout in milliseconds (optional)
 */
const GenericErrorAlert = ({ 
  show, 
  title, 
  errorCode, 
  onConfirm, 
  confirmBtnBsStyle = "danger", 
  timeout 
}) => {
  const dispatch = useDispatch();

  const handleConfirm = () => {
    // Default behavior: clear generic error
    dispatch({
      type: "GENERIC_ERROR"
    });
    
    // Call custom confirm handler if provided
    if (onConfirm) {
      onConfirm();
    }
  };

  return (
    <SweetAlert
      error
      title={title}
      show={show}
      onConfirm={handleConfirm}
      confirmBtnBsStyle={confirmBtnBsStyle}
      timeout={timeout}
    >
      {errorCode && `Error Code: ${errorCode}`}
    </SweetAlert>
  );
};

/**
 * Reusable Generic Warning/Confirmation Alert Component
 * 
 * @param {Object} props
 * @param {boolean} props.show - Whether to show the alert
 * @param {string} props.title - Alert title (default: "Are you sure?")
 * @param {string} props.message - Alert message content
 * @param {string} props.confirmBtnText - Confirm button text (default: "Yes, proceed!")
 * @param {string} props.confirmBtnBsStyle - Confirm button style (default: "danger")
 * @param {string} props.cancelBtnBsStyle - Cancel button style (default: "default")
 * @param {Function} props.onConfirm - Function to call on confirm
 * @param {Function} props.onCancel - Function to call on cancel
 * @param {boolean} props.showCancel - Whether to show cancel button (default: true)
 */
const GenericWarningAlert = ({ 
  show, 
  title = "Are you sure?", 
  message, 
  confirmBtnText = "Yes, proceed!", 
  confirmBtnBsStyle = "danger", 
  cancelBtnBsStyle = "default",
  onConfirm, 
  onCancel,
  showCancel = true
}) => {
  return (
    <SweetAlert
      warning
      showCancel={showCancel}
      show={show}
      confirmBtnText={confirmBtnText}
      confirmBtnBsStyle={confirmBtnBsStyle}
      cancelBtnBsStyle={cancelBtnBsStyle}
      title={title}
      onConfirm={onConfirm}
      onCancel={onCancel}
    >
      {message}
    </SweetAlert>
  );
};

/**
 * Reusable Generic Success Alert Component
 * 
 * @param {Object} props
 * @param {boolean} props.show - Whether to show the alert
 * @param {string} props.title - Success title (default: "Success!")
 * @param {string} props.message - Success message content
 * @param {string} props.confirmBtnBsStyle - Button style (default: "info")
 * @param {Function} props.onConfirm - Function to call on confirm
 * @param {number} props.timeout - Auto-close timeout in milliseconds (optional)
 */
const GenericSuccessAlert = ({ 
  show, 
  title = "Success!", 
  message, 
  confirmBtnBsStyle = "info", 
  onConfirm,
  timeout 
}) => {
  return (
    <SweetAlert
      success
      title={title}
      show={show}
      onConfirm={onConfirm}
      confirmBtnBsStyle={confirmBtnBsStyle}
      timeout={timeout}
    >
      {message}
    </SweetAlert>
  );
};

export { GenericErrorAlert, GenericWarningAlert, GenericSuccessAlert };
export default GenericErrorAlert; // Default export for backward compatibility
